import { Injectable, inject } from '@angular/core';
import { NavigationSkipped, NavigationStart, Router } from '@angular/router';

/**
 * Quién pidió la navegación que se está resolviendo: el **chrome** de la aplicación —menú lateral,
 * migas de pan, logotipo, atrás/adelante del navegador— o un **enlace de la propia pantalla**.
 *
 * <h3>Para qué</h3>
 * El árbol de navegación nace al salir de la ficha de un registro **por un enlace suyo**. Irse por
 * el menú lateral no es salir a mirar: es irse. Sin esta distinción, abrir el menú desde cualquier
 * ficha abriría un árbol.
 *
 * <h3>Por qué lo declara el chrome y no se deduce</h3>
 * El router no dice quién originó una navegación. Interceptar los clics en `[routerLink]` del
 * contenido sería frágil —queda fuera toda navegación programática, que es la mayoría de los enlaces
 * de una ficha—, mientras que los sitios de chrome son tres y están todos en `layout`. Se marca el
 * caso raro, no el común.
 *
 * <h3>La marca se gasta al empezar la navegación</h3>
 * En `NavigationStart` se resuelve de quién es la navegación en curso y la marca pendiente se borra.
 * Así una marca que nadie llegue a usar —porque el enlace del menú apuntaba a la pantalla en la que
 * ya se estaba y Angular no navega— no tiñe la siguiente navegación, que sí sería de la pantalla.
 *
 * <h3>La marca vale también para la navegación que acaba de arrancar en el mismo gesto</h3>
 * Las migas y el menú lateral son enlaces `[routerLink]` con un `(click)` que marca. Pero el
 * manejador de `routerLink` es de la directiva y se registra ANTES que el del template, así que el
 * orden real de un clic es: `routerLink` navega → el router emite `NavigationStart` (síncrono) →
 * el `(click)` marca. La marca llegaba tarde: la navegación ya estaba clasificada como «de la
 * pantalla» y subir por las migas desde una ficha estrenaba un árbol. Por eso, si `marcarChrome()`
 * llega mientras la navegación que acaba de arrancar sigue en el mismo tick síncrono, se aplica a
 * ESA navegación en vez de quedar pendiente para la siguiente. La ventana se cierra en un microtask:
 * lo justo para cubrir los manejadores del mismo clic y nada más.
 */
/**
 * Qué parte del chrome pidió la navegación. Importa porque no todo el chrome recibe el mismo trato:
 * salir de una ficha por el **menú** conserva la sesión de la tarea (es «deambular»), mientras que
 * subir por las **migas** es ir hacia atrás y sigue la regla de la flecha «volver» (por debajo del
 * umbral retira la sesión). `'arbol'` es cerrar el árbol entero, que sale de la pantalla en la que se
 * está y por eso también es ir hacia atrás. `'chrome'` es el resto: logotipo, paleta, la propia flecha
 * de volver, los saltos del carril.
 */
export type ViaChrome = 'menu' | 'migas' | 'arbol' | 'chrome';

@Injectable({ providedIn: 'root' })
export class OrigenNavegacionService {
  private pendiente: ViaChrome | null = null;
  private via: ViaChrome | null = null;
  /** Una navegación acaba de emitir `NavigationStart` en este mismo tick síncrono. */
  private arrancando = false;

  constructor() {
    // `events?`: los tests de componente montan routers falsos que no publican eventos. Sin ellos no
    // hay nada que clasificar y la marca pendiente se queda como esté, que es inocuo.
    inject(Router).events?.subscribe((e) => {
      // Enlace del chrome a la pantalla en la que ya se está: el router no navega y emite
      // `NavigationSkipped` sin `NavigationStart`. La marca pendiente se gasta igual, o teñiría la
      // siguiente navegación, que sí sería de la pantalla.
      if (e instanceof NavigationSkipped) {
        this.pendiente = null;
        return;
      }
      if (!(e instanceof NavigationStart)) return;
      // `popstate` es atrás/adelante del navegador: tampoco es un enlace de la pantalla.
      this.via = this.pendiente ?? (e.navigationTrigger !== 'imperative' ? 'chrome' : null);
      this.pendiente = null;
      this.arrancando = true;
      queueMicrotask(() => (this.arrancando = false));
    });
  }

  /**
   * Lo llaman el menú lateral, las migas y el logotipo justo antes de navegar, o en el mismo clic en
   * el que `routerLink` ya ha navegado. `via` dice qué parte del chrome es; quien no necesite
   * distinguirse deja el valor por defecto.
   */
  marcarChrome(via: ViaChrome = 'chrome'): void {
    if (this.arrancando) {
      this.via = via;
      return;
    }
    this.pendiente = via;
  }

  /** True si la navegación que se está resolviendo la pidió el chrome y no la pantalla. */
  vieneDelChrome(): boolean {
    return this.via !== null;
  }

  /**
   * True si la navegación en curso la pidieron las **migas de pan**. Es la señal que usa el guard de
   * salida de las fichas para tratar esa salida como «ir hacia atrás» y no como deambular por el menú.
   */
  vieneDeLasMigas(): boolean {
    return this.via === 'migas';
  }

  /**
   * True si la navegación en curso es una salida **hacia atrás** pedida por el chrome: subir por las
   * migas o cerrar el árbol entero. El guard de salida de la ficha la trata como la flecha «volver».
   */
  vieneHaciaAtras(): boolean {
    return this.via === 'migas' || this.via === 'arbol';
  }
}
